import React, { useState, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../../shared/permissions';
import { Button, Alert } from 'react-bootstrap';
import { useQueryClient } from '@tanstack/react-query';
import StatusBadge from '../../components/StatusBadge';
import {
    IconCar,
    IconCash,
    IconContract,
    IconFile,
    IconMoneybag,
    IconPackages,
    IconUsers,
    IconUsersGroup
} from '@tabler/icons-react';
import { PageLoader, InlineLoader } from '../../components/LoadingSpinner';
import { api } from '../../shared/api';
import { useContract, contractKeys } from '../../entities/contract';
import { useCurrency } from '../../shared/CurrencyContext';
import { useDateFormat } from '../../shared/DateFormatContext';
import { COLOR_SUCCESS, COLOR_WARNING, COLOR_PRIMARY, COLOR_DANGER, COLOR_SECONDARY } from '../../shared/colorUtils';
import Card from '../../components/Card';
import Modal from '../../components/Modal';
import ContractItems from './components/ContractItems';
import ContractVehicles from './components/ContractVehicles';
import ContractStaff from './components/ContractStaff';
import ContractFiles from './components/ContractFiles';
import ContractOffers from './components/ContractOffers';
import ContractDeploy from './components/ContractDeploy';
import ContractReturn from './components/ContractReturn';
import { useConfirm } from '../../shared/ConfirmContext';

const ContractDetail = () => {
  const { contractId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { canManage } = usePermissions();
  const { formatCurrency } = useCurrency();

  const queryClient = useQueryClient();
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [conversionLoading, setConversionLoading] = useState(false);
  const [loadingSheetGenerating, setLoadingSheetGenerating] = useState(false);
  const [customProjectName, setCustomProjectName] = useState('');
  const [showConversionModal, setShowConversionModal] = useState(false);

  const { data: contract, isLoading: loading } = useContract(contractId);

  const statistics = useMemo(() => {
    if (!contract) return null;
    const contractCurrency = contract.currency_detail || null;
    return {
      total_items: contract.items?.length || 0,
      total_vehicles: contract.vehicles?.length || 0,
      total_staff: contract.staff?.length || 0,
      total_files: contract.files?.length || 0,
      total_offers: contract.offers?.length || 0,
      budget_formatted: contract.budget
        ? formatCurrency(contract.budget, { currency: contractCurrency })
        : '-',
      duration_days: contract.start_date && contract.end_date
        ? Math.ceil((new Date(contract.end_date) - new Date(contract.start_date)) / (1000 * 60 * 60 * 24))
        : null,
    };
  }, [contract, formatCurrency]);

  const handleToggleLock = async () => {
    try {
      await api.patch(`contracts/api/contracts/${contractId}/`, { locked: !contract.locked });
      queryClient.invalidateQueries({ queryKey: contractKeys.detail(contractId) });
    } catch (err) {
      console.error('Error toggling lock:', err);
      setError(t('contracts.lockError'));
    }
  };

  const handleDownloadLoadingSheet = async () => {
    try {
      setLoadingSheetGenerating(true);
      const blob = await api.get(`contracts/api/contracts/${contractId}/loading_sheet/`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const today = new Date().toISOString().slice(0, 10);
      link.href = url;
      link.download = `loading_sheet_${contractId}_${today}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error generating loading sheet:', err);
      setError(t('contracts.errorLoadingSheet'));
      setTimeout(() => setError(null), 5000);
    } finally {
      setLoadingSheetGenerating(false);
    }
  };

  const handleDelete = async () => {
    if (await confirm(t('contracts.confirmDelete'))) {
      try {
        await api.delete(`contracts/api/contracts/${contractId}/`);
        navigate('/contracts');
      } catch (err) {
        console.error('Error deleting contract:', err);
        setError('Failed to delete contract. Please try again.');
      }
    }
  };

  const handleContractToProjectConversion = async (e) => {
    e.preventDefault();

    if (!await confirm(t('contracts.conversion.backToProject.confirmConversion'))) {
      return;
    }

    try {
      setConversionLoading(true);
      const conversionData = {
        name: customProjectName || undefined
      };

      const response = await api.post(`contracts/api/contracts/${contractId}/create_project/`, conversionData);

      // Reset form and close modal
      setCustomProjectName('');
      setShowConversionModal(false);

      // Show success message and redirect to the new project
      alert(t('contracts.conversion.backToProject.success'));

      // Redirect to the new project if we have the project ID
      if (response.data?.project?.id) {
        navigate(`/projects/${response.data.project.id}`);
      } else {
        navigate('/projects');
      }
    } catch (err) {
      console.error('Error converting contract to project:', err);
      setError(err.response?.data?.error || t('contracts.conversion.backToProject.error'));
    } finally {
      setConversionLoading(false);
    }
  };

  const handleShowConversionModal = () => {
    resetProjectConversionForm();
    setShowConversionModal(true);
  };

  const handleCloseConversionModal = () => {
    setShowConversionModal(false);
    resetProjectConversionForm();
  };

  const resetProjectConversionForm = () => {
    setCustomProjectName('');
    setError(null);
  };

  const { formatDate: fmtDate, formatDateTime: fmtDateTime } = useDateFormat();

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return fmtDate(dateString);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return fmtDateTime(dateString);
  };

  const getStatusHex = (statusName) => {
    const status = statusName?.toLowerCase();
    switch (status) {
      case 'aktivní':
      case 'active':    return COLOR_SUCCESS;
      case 'plánováno':
      case 'planned':   return COLOR_WARNING;
      case 'dokončeno':
      case 'completed': return COLOR_PRIMARY;
      case 'zrušeno':
      case 'cancelled': return COLOR_DANGER;
      default:          return COLOR_SECONDARY;
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  if (error) {
    return (
      <div className="page-wrapper">
        <div className="container-xl">
          <Alert variant="danger">{error}</Alert>
        </div>
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="page-wrapper">
        <div className="container-xl">
          <Alert variant="warning">Contract not found.</Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <div className="page-pretitle">
                <Link to="/contracts" className="text-muted">
                  {t('menu.contracts')}
                </Link>
              </div>
              <h2 className="page-title d-flex align-items-center">
                {contract.name}
                {contract.locked && (
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon ms-2 text-warning">
                    <rect width="18" height="11" x="3" y="11" rx="2" ry="2"></rect>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                  </svg>
                )}
                <StatusBadge color={getStatusHex(contract.status_name)} label={contract.status_name} className="ms-2" />
              </h2>
            </div>
              <div className="col-auto ms-auto d-print-none">
              <div className="btn-list">
                <Button
                  variant="outline-secondary"
                  onClick={handleDownloadLoadingSheet}
                  disabled={loadingSheetGenerating}
                >
                  <i className="ti ti-truck me-1"></i>
                  {loadingSheetGenerating ? t('contracts.generatingLoadingSheet') : t('contracts.downloadLoadingSheet')}
                </Button>
                {canManage && (
                  <Button
                    variant="outline-primary"
                    onClick={handleShowConversionModal}
                  >
                    <i className="ti ti-file-arrow-right me-1"></i>
                    {t('contracts.convertToProject')}
                  </Button>
                )}
                {canManage && (
                  <Button
                    variant={`outline-${contract.locked ? 'success' : 'warning'}`}
                    onClick={handleToggleLock}
                    title={contract.locked ? t('contracts.unlock') : t('contracts.lock')}
                  >
                    <i className={`ti ti-${contract.locked ? 'lock-open' : 'lock'} me-1`}></i>
                    {contract.locked ? t('contracts.unlock') : t('contracts.lock')}
                  </Button>
                )}
                {canManage && (
                  <Button
                    variant="outline-danger"
                    onClick={handleDelete}
                    disabled={contract.locked}
                  >
                    <i className="ti ti-trash me-1"></i>
                    {t('common.delete')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body">
        <div className="container-xl">
          {error && (
            <Alert variant="danger">{error}</Alert>
          )}

          {/* Read-only notice */}
          <div className="alert alert-info" role="alert">
            <div className="d-flex align-items-start">
              <div className="ms-3">
                <h4 className="alert-title">{t('contracts.contractReadOnly')}</h4>
                <div className="text-muted">
                  {t('contracts.contractReadOnly')}. {t('contracts.convertToProject')}.
                </div>
              </div>
            </div>
          </div>

          <div className="row row-deck row-cards justify-content-center">
            <div className="col-sm-6 col-lg-2">
              <div className="card card-sm">
                <div className="card-body">
                  <div className="d-flex flex-column align-items-center text-center gap-2">
                    <span className="bg-primary text-white avatar">
                      <IconPackages size={20} stroke={2} />
                    </span>
                    <div>
                      <div className="font-weight-medium">
                        {statistics?.total_items || 0} {t('contracts.items')}
                      </div>
                      <div className="text-muted small">
                        {t('contracts.totalItems')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-sm-6 col-lg-2">
              <div className="card card-sm">
                <div className="card-body">
                  <div className="d-flex flex-column align-items-center text-center gap-2">
                    <span className="bg-success text-white avatar">
                      <IconCar size={20} stroke={2} />
                    </span>
                    <div>
                      <div className="font-weight-medium">
                        {statistics?.total_vehicles || 0} {t('contracts.vehicles')}
                      </div>
                      <div className="text-muted small">
                        {t('contracts.totalVehicles')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-sm-6 col-lg-2">
              <div className="card card-sm">
                <div className="card-body">
                  <div className="d-flex flex-column align-items-center text-center gap-2">
                    <span className="bg-info text-white avatar">
                      <IconUsersGroup size={20} stroke={2} />
                    </span>
                    <div>
                      <div className="font-weight-medium">
                        {statistics?.total_staff || 0} {t('contracts.staff')}
                      </div>
                      <div className="text-muted small">
                        {t('contracts.totalStaff')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-sm-6 col-lg-2">
              <div className="card card-sm">
                <div className="card-body">
                  <div className="d-flex flex-column align-items-center text-center gap-2">
                    <span className="bg-warning text-white avatar">
                      <IconFile size={20} stroke={2} />
                    </span>
                    <div>
                      <div className="font-weight-medium">
                        {statistics?.total_files || 0} {t('contracts.files')}
                      </div>
                      <div className="text-muted small">
                        {t('contracts.totalFiles')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-sm-6 col-lg-2">
              <div className="card card-sm">
                <div className="card-body">
                  <div className="d-flex flex-column align-items-center text-center gap-2">
                    <span className="bg-secondary text-white avatar">
                      <IconContract size={20} stroke={2} />
                    </span>
                    <div>
                      <div className="font-weight-medium">
                        {statistics?.total_offers || 0} {t('contracts.offers')}
                      </div>
                      <div className="text-muted small">
                        {t('contracts.totalOffers')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-sm-6 col-lg-2">
              <div className="card card-sm">
                <div className="card-body">
                  <div className="d-flex flex-column align-items-center text-center gap-2">
                    <span className="bg-success text-white avatar">
                      <IconCash size={20} stroke={2} />
                    </span>
                    <div>
                      <div className="font-weight-medium">
                        {statistics?.budget_formatted}
                      </div>
                      <div className="text-muted small">
                        {t('contracts.fields.budget')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contract Information */}
            <div className="col-12">
              <Card>
                <div className="card-header">
                  <ul className="nav nav-tabs card-header-tabs" role="tablist">
                    <li className="nav-item">
                      <a
                        className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`}
                        href="#"
                        onClick={(e) => { e.preventDefault(); setActiveTab('overview'); }}
                      >
                        {t('contracts.tabs.overview')}
                      </a>
                    </li>
                    <li className="nav-item">
                      <a
                        className={`nav-link ${activeTab === 'items' ? 'active' : ''}`}
                        href="#"
                        onClick={(e) => { e.preventDefault(); setActiveTab('items'); }}
                      >
                        {t('contracts.tabs.items')} ({statistics?.total_items || 0})
                      </a>
                    </li>
                    <li className="nav-item">
                      <a
                        className={`nav-link ${activeTab === 'vehicles' ? 'active' : ''}`}
                        href="#"
                        onClick={(e) => { e.preventDefault(); setActiveTab('vehicles'); }}
                      >
                        {t('contracts.tabs.vehicles')} ({statistics?.total_vehicles || 0})
                      </a>
                    </li>
                    <li className="nav-item">
                      <a
                        className={`nav-link ${activeTab === 'staff' ? 'active' : ''}`}
                        href="#"
                        onClick={(e) => { e.preventDefault(); setActiveTab('staff'); }}
                      >
                        {t('contracts.tabs.staff')} ({statistics?.total_staff || 0})
                      </a>
                    </li>
                    <li className="nav-item">
                      <a
                        className={`nav-link ${activeTab === 'files' ? 'active' : ''}`}
                        href="#"
                        onClick={(e) => { e.preventDefault(); setActiveTab('files'); }}
                      >
                        {t('contracts.tabs.files')} ({statistics?.total_files || 0})
                      </a>
                    </li>
                    <li className="nav-item">
                      <a
                        className={`nav-link ${activeTab === 'offers' ? 'active' : ''}`}
                        href="#"
                        onClick={(e) => { e.preventDefault(); setActiveTab('offers'); }}
                      >
                        {t('contracts.tabs.offers')} ({statistics?.total_offers || 0})
                      </a>
                    </li>
                    <li className="nav-item">
                      <a
                        className={`nav-link ${activeTab === 'deploy' ? 'active' : ''}`}
                        href="#"
                        onClick={(e) => { e.preventDefault(); setActiveTab('deploy'); }}
                      >
                        <i className="ti ti-truck me-1"></i>
                        {t('contracts.tabs.deploy')}
                      </a>
                    </li>
                    <li className="nav-item">
                      <a
                        className={`nav-link ${activeTab === 'return' ? 'active' : ''}`}
                        href="#"
                        onClick={(e) => { e.preventDefault(); setActiveTab('return'); }}
                      >
                        <i className="ti ti-arrow-back-up me-1"></i>
                        {t('contracts.tabs.return')}
                      </a>
                    </li>
                  </ul>
                </div>
                <div className="card-body">
                  {activeTab === 'overview' && (
                    <div className="row">
                      <div className="col-md-6">
                        <h4>{t('contracts.basicInfo')}</h4>
                        <div className="table-responsive">
                        <table className="table table-borderless">
                          <tbody>
                            <tr>
                              <td><strong>{t('contracts.fields.name')}:</strong></td>
                              <td>{contract.name}</td>
                            </tr>
                            <tr>
                              <td><strong>{t('contracts.fields.description')}:</strong></td>
                              <td>{contract.description || '-'}</td>
                            </tr>
                            <tr>
                              <td><strong>{t('contracts.fields.contractor')}:</strong></td>
                              <td>{contract.contractor?.company_name || '-'}</td>
                            </tr>
                            <tr>
                              <td><strong>{t('contracts.fields.customer')}:</strong></td>
                              <td>{contract.customer?.company_name || '-'}</td>
                            </tr>
                            <tr>
                              <td><strong>{t('contracts.fields.location')}:</strong></td>
                              <td>{contract.location || '-'}</td>
                            </tr>
                            <tr>
                              <td><strong>{t('contracts.fields.version')}:</strong></td>
                              <td>{contract.version}</td>
                            </tr>
                            <tr>
                              <td><strong>{t('contracts.fields.locked')}:</strong></td>
                              <td>{contract.locked ? t('common.yes') : t('common.no')}</td>
                            </tr>
                          </tbody>
                        </table>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <h4>{t('contracts.datesAndFinancials')}</h4>
                        <div className="table-responsive">
                        <table className="table table-borderless">
                          <tbody>
                            <tr>
                              <td><strong>{t('contracts.fields.startDate')}:</strong></td>
                              <td>{formatDate(contract.start_date)}</td>
                            </tr>
                            <tr>
                              <td><strong>{t('contracts.fields.endDate')}:</strong></td>
                              <td>{formatDate(contract.end_date)}</td>
                            </tr>
                            {statistics?.duration_days && (
                              <tr>
                                <td><strong>{t('contracts.fields.duration')}:</strong></td>
                                <td>{statistics.duration_days} {t('common.days')}</td>
                              </tr>
                            )}
                            <tr>
                              <td><strong>{t('contracts.fields.budget')}:</strong></td>
                              <td>{statistics?.budget_formatted}</td>
                            </tr>
                            <tr>
                              <td><strong>{t('contracts.fields.currency')}:</strong></td>
                              <td>{contract.currency_detail ? `${contract.currency_detail.name} (${contract.currency_detail.code})` : '-'}</td>
                            </tr>
                            <tr>
                              <td><strong>{t('contracts.fields.taxRate')}:</strong></td>
                              <td>{contract.tax_rate}%</td>
                            </tr>
                            <tr>
                              <td><strong>{t('contracts.fields.status')}:</strong></td>
                              <td>
                                <StatusBadge color={getStatusHex(contract.status_name)} label={contract.status_name} />
                              </td>
                            </tr>
                            <tr>
                              <td><strong>{t('contracts.fields.type')}:</strong></td>
                              <td>{contract.type_name || '-'}</td>
                            </tr>
                            <tr>
                              <td><strong>{t('common.createdAt')}:</strong></td>
                              <td>{formatDateTime(contract.created_at)}</td>
                            </tr>
                            <tr>
                              <td><strong>{t('common.updatedAt')}:</strong></td>
                              <td>{formatDateTime(contract.updated_at)}</td>
                            </tr>
                          </tbody>
                        </table>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'items' && (
                    <ContractItems
                      contractId={contractId}
                      items={contract.items || []}
                      readOnly={true}
                      onUpdate={() => queryClient.invalidateQueries({ queryKey: contractKeys.detail(contractId) })}
                    />
                  )}

                  {activeTab === 'vehicles' && (
                    <ContractVehicles
                      contractId={contractId}
                      vehicles={contract.vehicles || []}
                      readOnly={true}
                      onUpdate={() => queryClient.invalidateQueries({ queryKey: contractKeys.detail(contractId) })}
                    />
                  )}

                  {activeTab === 'staff' && (
                    <ContractStaff
                      contractId={contractId}
                      staff={contract.staff || []}
                      readOnly={true}
                      onUpdate={() => queryClient.invalidateQueries({ queryKey: contractKeys.detail(contractId) })}
                    />
                  )}

                  {activeTab === 'files' && (
                    <ContractFiles
                      contractId={contractId}
                      files={contract.files || []}
                      readOnly={true}
                      onUpdate={() => queryClient.invalidateQueries({ queryKey: contractKeys.detail(contractId) })}
                    />
                  )}

                  {activeTab === 'offers' && (
                    <ContractOffers
                      contractId={contractId}
                      offers={contract.offers || []}
                      readOnly={true}
                      onUpdate={() => queryClient.invalidateQueries({ queryKey: contractKeys.detail(contractId) })}
                    />
                  )}

                  {activeTab === 'deploy' && (
                    <ContractDeploy
                      contractId={contractId}
                      items={contract.items || []}
                    />
                  )}

                  {activeTab === 'return' && (
                    <ContractReturn
                      contractId={contractId}
                    />
                  )}
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Contract to Project Conversion Modal */}
      <Modal
        id="contractToProjectModal"
        title={t('contracts.conversion.backToProject.title')}
        size="md"
        show={showConversionModal}
        onHide={handleCloseConversionModal}
      >
        <form onSubmit={handleContractToProjectConversion}>
          <div className="modal-body">
            {error && (
              <Alert variant="danger">{error}</Alert>
            )}

            <div className="mb-3">
              <label className="form-label" htmlFor="customProjectName">{t('contracts.conversion.backToProject.customName')}</label>
              <input
                type="text"
                id="customProjectName"
                className="form-control"
                value={customProjectName}
                onChange={(e) => setCustomProjectName(e.target.value)}
                placeholder={t('contracts.conversion.backToProject.customNamePlaceholder')}
              />
              <div className="form-hint">
                {t('contracts.conversion.backToProject.customNamePlaceholder')}
              </div>
            </div>

            <div className="alert alert-info" role="alert">
              <div className="d-flex align-items-start">
                <div>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon alert-icon">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M12 16v-4"></path>
                    <path d="M12 8h.01"></path>
                  </svg>
                </div>
                <div className="ms-3">
                  {t('contracts.conversion.backToProject.confirmConversion')}
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={handleCloseConversionModal}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={conversionLoading}>
              {conversionLoading ? (
                <>
                  <InlineLoader className="me-2" />
                  {t('contracts.conversion.converting')}
                </>
              ) : (
                t('contracts.conversion.backToProject.convert')
              )}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ContractDetail;
